/**
 * Bloom's Taxonomy levels — single source of truth for the targeter and
 * the Objective tool's verb bank. Ported from the workbook source.
 */

export const LEVELS = {
  remember: {
    num: 1,
    key: 'remember',
    name: 'Remember',
    color: '#c45c3a',
    bg: '#fdf4f0',
    border: '#e8b9a8',
    textDark: '#8a3e1a',
    tagline:
      'Your learners need to reliably recall key information. Focus on building strong memory and recognition.',
    explanation:
      'Based on your answers, your learners are starting fresh with relatively clear, well-defined information they need to be able to retrieve. The foundation before anything else can happen is solid recall — getting the right facts, steps, or terms into long-term memory. Activities like spaced repetition, quizzes, labelling, and matching exercises work well at this level.',
    verbs: ['define', 'identify', 'label', 'list', 'match', 'memorise', 'name', 'recall', 'recognise', 'recite', 'repeat', 'reproduce', 'select', 'state'],
    example:
      'Given a policy handbook and review activities, you will identify the five steps in the workplace incident reporting process in the correct order, as evaluated by the trainer using a sequencing checklist.',
    also: ['understand', 'apply'],
  },
  understand: {
    num: 2,
    key: 'understand',
    name: 'Understand',
    color: '#7a6b1e',
    bg: '#faf8ee',
    border: '#ddd5a0',
    textDark: '#5a4e10',
    tagline:
      'Your learners need to genuinely grasp the concept — not just memorise it, but be able to explain and work with it.',
    explanation:
      "Your answers suggest the goal is conceptual understanding — learners need to make meaning, not just recall. They should be able to explain the idea in their own words, recognise new examples, or connect the concept to situations they haven't explicitly been taught.",
    verbs: ['clarify', 'classify', 'compare', 'describe', 'explain', 'exemplify', 'illustrate', 'interpret', 'infer', 'paraphrase', 'predict', 'summarise', 'translate'],
    example:
      'Given readings and a facilitated discussion, you will explain the difference between formative and summative evaluation in your own words, providing one example of each from your own training context, as evaluated by a peer using a response checklist.',
    also: ['remember', 'apply'],
  },
  apply: {
    num: 3,
    key: 'apply',
    name: 'Apply',
    color: '#2d6a4f',
    bg: '#edf7f2',
    border: '#a8d5be',
    textDark: '#1a4a35',
    tagline:
      "Your learners need to take what they've learned and actually use it — in a scenario, simulation, or real task.",
    explanation:
      "Your answers point clearly to application: there's a job task your learners need to perform, and training success means they can do it. This requires practice under realistic conditions with feedback — not just hearing about the skill.",
    verbs: ['apply', 'calculate', 'carry out', 'construct', 'demonstrate', 'execute', 'implement', 'operate', 'perform', 'practise', 'produce', 'solve', 'use'],
    example:
      'Given a role-play scenario, templates, and facilitator feedback, you will conduct a coaching conversation with a simulated team member using the GROW model, as evaluated by the facilitator using a coaching observation checklist.',
    also: ['understand', 'analyse'],
  },
  analyse: {
    num: 4,
    key: 'analyse',
    name: 'Analyse',
    color: '#1a5a7a',
    bg: '#edf5fa',
    border: '#a0c8de',
    textDark: '#103d55',
    tagline:
      'Your learners need to examine situations critically — breaking things down, spotting patterns, distinguishing what matters.',
    explanation:
      'Analysis is the right target when learners face situational complexity — where reading the context, distinguishing relevant from irrelevant, and identifying root causes is what success requires.',
    verbs: ['analyse', 'attribute', 'categorise', 'compare', 'contrast', 'deconstruct', 'differentiate', 'distinguish', 'examine', 'identify', 'investigate', 'outline', 'separate'],
    example:
      'Given three case studies and a facilitated discussion, you will analyse each scenario to distinguish between a performance issue and a conduct issue, identifying the key indicators that support your conclusion, as evaluated by the facilitator using a case analysis rubric.',
    also: ['apply', 'evaluate'],
  },
  evaluate: {
    num: 5,
    key: 'evaluate',
    name: 'Evaluate',
    color: '#6b3a8a',
    bg: '#f5eefc',
    border: '#cdb4e8',
    textDark: '#4a256a',
    tagline:
      'Your learners need to make and justify judgements — assessing quality, recommending action, or defending a position.',
    explanation:
      'Evaluation is the right level when the job requires more than doing — it requires judging. Your learners need to weigh evidence, apply criteria, and make defensible decisions.',
    verbs: ['appraise', 'argue', 'assess', 'conclude', 'critique', 'decide', 'defend', 'evaluate', 'judge', 'justify', 'prioritise', 'rate', 'recommend', 'validate'],
    example:
      "Given a sample training programme and an evaluation rubric, you will assess the programme's alignment with adult learning principles, justifying your rating for each criterion with specific evidence, as evaluated by yourself using a programme evaluation checklist.",
    also: ['analyse', 'create'],
  },
  create: {
    num: 6,
    key: 'create',
    name: 'Create',
    color: '#8a3e1a',
    bg: '#fdf0e8',
    border: '#e8c4a0',
    textDark: '#6a2e10',
    tagline:
      "Your learners need to produce something original — a plan, a design, a programme, or a product that didn't exist before.",
    explanation:
      "Creation is the highest and most demanding level. Your answers suggest the training outcome is a tangible product — something learners design, build, or produce by drawing on everything they've learned.",
    verbs: ['assemble', 'brainstorm', 'build', 'compose', 'construct', 'create', 'design', 'develop', 'devise', 'formulate', 'generate', 'invent', 'plan', 'produce'],
    example:
      'Given readings, templates, case studies, practice, and feedback, you will design a complete learning objective for a training programme in your own context, meeting all four standards of the objective-writing framework, as evaluated by yourself using the objective-writing checklist.',
    also: ['evaluate', 'analyse'],
  },
};

/**
 * Recommendation logic from the source workbook. Takes the four answers
 * and returns a level key.
 */
export function recommendLevel({ q1, q2, q3, q4 }) {
  let level = q2;
  // Novices in a short slot can't realistically hit Evaluate or Create — drop to Analyse
  if (q1 === 'novice' && (level === 'evaluate' || level === 'create') && q4 === 'short') level = 'analyse';
  // Novices targeting Create with only a half-day → bump to Evaluate
  if (q1 === 'novice' && level === 'create' && q4 === 'half') level = 'evaluate';
  // Experienced practitioners shouldn't be aiming at Remember if the task is clear
  if (q1 === 'experienced' && level === 'remember' && q3 === 'clear') level = 'understand';
  // Experienced practitioners with non-clear tasks should hit Apply, not just Understand
  if (q1 === 'experienced' && level === 'understand' && q3 !== 'clear') level = 'apply';
  // Short slot can't sustain Create or Evaluate regardless
  if (q4 === 'short' && (level === 'create' || level === 'evaluate')) level = 'analyse';
  return level;
}

/* Question definitions — the four wizard steps */
export const QUESTIONS = [
  {
    id: 'q1',
    badge: 1,
    label: 'About your learners',
    question: "What best describes your learners' starting point with this topic?",
    sub: 'Think about where they are right now — before your training begins.',
    options: [
      { value: 'novice',     title: 'Complete beginners',         desc: "Little or no prior knowledge of this topic. It's largely new territory." },
      { value: 'familiar',   title: 'Some familiarity',           desc: "They've been exposed to the topic — they know the basics but haven't applied it much." },
      { value: 'experienced', title: 'Experienced practitioners', desc: 'They already do this work. The training is about deepening, refining, or extending what they know.' },
    ],
  },
  {
    id: 'q2',
    badge: 2,
    label: 'The real-world outcome',
    question: 'After the presentation, what do you most want learners to be able to do?',
    sub: "Choose the option that best fits — even if it isn't a perfect match.",
    hint: 'Think about what success looks like on the job, not just in the workshop.',
    options: [
      { value: 'remember',  title: 'Know and recall key information',                  desc: 'Policies, definitions, steps, terms — things they need to retrieve reliably.' },
      { value: 'understand', title: 'Understand and explain concepts',                 desc: 'Grasp the why and explain it to others in their own words.' },
      { value: 'apply',     title: 'Use a skill or procedure on the job',              desc: 'A task they need to actually perform — an interview, a technique, a process.' },
      { value: 'analyse',   title: 'Analyse situations and make sense of complexity',  desc: 'Situations with no obvious right answer — they examine, compare, figure things out.' },
      { value: 'evaluate',  title: 'Make judgements and recommendations',              desc: 'Assess quality, weigh options, defend a position, advise others.' },
      { value: 'create',    title: 'Design or produce something new',                  desc: 'The output is the learning — a plan, a design, a proposal, a programme.' },
    ],
  },
  {
    id: 'q3',
    badge: 3,
    label: 'The nature of the task',
    question: 'How complex or ambiguous is the task your learners will face?',
    sub: 'This helps confirm the right level — some tasks seem complex but are actually straightforward to execute.',
    options: [
      { value: 'clear',       title: 'Clear and well-defined',                desc: 'A right answer or correct procedure. Success is relatively easy to recognise.' },
      { value: 'situational', title: 'Situational — depends on context',      desc: 'The right approach shifts. Learners need to read the situation and adapt.' },
      { value: 'complex',     title: 'Complex — requires judgement and synthesis', desc: 'Competing priorities, unclear information, no obvious right answer.' },
    ],
  },
  {
    id: 'q4',
    badge: 4,
    label: 'Your context',
    question: 'How much time and practice opportunity does your training allow?',
    sub: "Higher levels of Bloom's require more time — this helps give you a realistic recommendation.",
    options: [
      { value: 'short', title: 'Short session (1–3 hours)',           desc: "Limited time. We need to be realistic about what's achievable." },
      { value: 'half',  title: 'Half or full day',                    desc: 'Enough time for some practice, discussion, and feedback.' },
      { value: 'multi', title: 'Multi-day or ongoing programme',      desc: 'Plenty of time for practice, iteration, coaching, and deeper learning.' },
    ],
  },
];
